import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { SortButton } from "@/components/admin/sort-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistance } from "date-fns";

import { supabase } from "../providers/supabase/supabase";
import { Linkedin, Mail, Phone, Trash2, UserPlus } from "lucide-react";
import {
  RecordContextProvider,
  ShowBase,
  useDelete,
  useListContext,
  useNotify,
  useRecordContext,
  useShowContext,
} from "ra-core";
import {
  Link,
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";

import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityLog } from "../activity/ActivityLog";
import { Avatar } from "../contacts/Avatar";
import { TagsList } from "../contacts/TagsList";
import { findDealLabel } from "../deals/deal";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SalesIntelTab } from "../sales-intel/SalesIntelTab";
import type { Company, Contact, Deal, Entity } from "../types";
import {
  AdditionalInfo,
  AddressInfo,
  CompanyAside,
  CompanyInfo,
  ContextInfo,
} from "./CompanyAside";
import { CompanyAvatar } from "./CompanyAvatar";

export const CompanyShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase>
      {isMobile ? <CompanyShowContentMobile /> : <CompanyShowContent />}
    </ShowBase>
  );
};

const CompanyShowContentMobile = () => {
  const { record, isPending } = useShowContext<Company>();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/" />
        <div className="flex flex-1">
          <Link to="/">
            <h1 className="text-xl font-semibold">Company</h1>
          </Link>
        </div>
      </MobileHeader>

      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <CompanyAvatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">{record.name}</h2>
            </div>
          </div>
        </div>
        <CompanyInfo record={record} />
        <AddressInfo record={record} />
        <ContextInfo record={record} />
        <AdditionalInfo record={record} />
      </MobileContent>
    </>
  );
};

const CompanyShowContent = () => {
  const { record, isPending } = useShowContext<Company>();
  const navigate = useNavigate();

  // Get tab from URL or default to "activity"
  const tabMatch = useMatch("/companies/:id/show/:tab");
  const currentTab = tabMatch?.params?.tab || "activity";

  const handleTabChange = (value: string) => {
    if (value === currentTab) return;
    if (value === "activity") {
      navigate(`/companies/${record?.id}/show`);
      return;
    }
    navigate(`/companies/${record?.id}/show/${value}`);
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <div className="flex mb-3">
              <CompanyAvatar />
              <h5 className="text-xl ml-2 flex-1">{record.name}</h5>
            </div>
            <Tabs defaultValue={currentTab} onValueChange={handleTabChange}>
              <TabsList className="flex w-full">
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="contacts">
                  {record.nb_contacts
                    ? record.nb_contacts === 1
                      ? "1 Contact"
                      : `${record.nb_contacts} Contacts`
                    : "No Contacts"}
                </TabsTrigger>
                {record.nb_deals ? (
                  <TabsTrigger value="deals">
                    {record.nb_deals === 1
                      ? "1 deal"
                      : `${record.nb_deals} deals`}
                  </TabsTrigger>
                ) : null}
                <TabsTrigger value="entities">
                  {record.nb_entities
                    ? record.nb_entities === 1
                      ? "1 Entity"
                      : `${record.nb_entities} Entities`
                    : "Entities"}
                </TabsTrigger>
                <TabsTrigger value="intel">Intel</TabsTrigger>
              </TabsList>
              <TabsContent value="activity" className="pt-2">
                <ActivityLog companyId={record.id} context="company" />
              </TabsContent>
              <TabsContent value="contacts">
                {record.nb_contacts ? (
                  <ReferenceManyField
                    reference="contacts_summary"
                    target="company_id"
                    sort={{ field: "last_name", order: "ASC" }}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-row justify-end space-x-2 mt-1">
                        {!!record.nb_contacts && (
                          <SortButton
                            fields={["last_name", "first_name", "last_seen"]}
                          />
                        )}
                        <CreateRelatedContactButton />
                      </div>
                      <ContactsIterator />
                    </div>
                  </ReferenceManyField>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row justify-end space-x-2 mt-1">
                      <CreateRelatedContactButton />
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="deals">
                {record.nb_deals ? (
                  <ReferenceManyField
                    reference="deals"
                    target="company_id"
                    sort={{ field: "name", order: "ASC" }}
                  >
                    <DealsIterator />
                  </ReferenceManyField>
                ) : null}
              </TabsContent>
              <TabsContent value="entities">
                <ReferenceManyField
                  reference="entities"
                  target="company_id"
                  sort={{ field: "name", order: "ASC" }}
                >
                  <EntitiesIterator />
                </ReferenceManyField>
              </TabsContent>
              <TabsContent value="intel">
                <SalesIntelTab />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <CompanyAside />
    </div>
  );
};

const ContactsIterator = () => {
  const location = useLocation();
  const { data: contacts, error, isPending, refetch } =
    useListContext<Contact>();
  const [expandedId, setExpandedId] = useState<string | number | null>(
    null,
  );

  if (isPending || error) return null;

  const now = Date.now();

  return (
    <div className="pt-0">
      {contacts.map((contact) => {
        const hasEmail = contact.email_jsonb?.some((e) => e.email);
        const hasPhone = contact.phone_jsonb?.some((p) => p.number);
        const hasLinkedIn = !!contact.linkedin_url;
        const isExpanded = expandedId === contact.id;
        return (
          <RecordContextProvider key={contact.id} value={contact}>
            <div className="p-0 text-sm border-b last:border-b-0">
              <div
                className="flex items-center hover:bg-muted py-2 transition-colors cursor-pointer"
                onClick={() =>
                  setExpandedId(isExpanded ? null : contact.id)
                }
              >
                <div className="mr-4">
                  <Avatar />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {`${contact.first_name} ${contact.last_name}`}
                    </span>
                    <div className="flex gap-1">
                      {hasEmail && (
                        <Mail className="h-3 w-3 text-muted-foreground" />
                      )}
                      {hasPhone && (
                        <Phone className="h-3 w-3 text-muted-foreground" />
                      )}
                      {hasLinkedIn && (
                        <Linkedin className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {contact.title}
                    {contact.nb_tasks
                      ? ` - ${contact.nb_tasks} task${
                          contact.nb_tasks > 1 ? "s" : ""
                        }`
                      : ""}
                    &nbsp; &nbsp;
                    <TagsList />
                  </div>
                </div>
                {contact.last_seen && (
                  <div className="text-right mr-2">
                    <div className="text-sm text-muted-foreground">
                      last activity{" "}
                      {formatDistance(contact.last_seen, now)} ago{" "}
                      <Status status={contact.status} />
                    </div>
                  </div>
                )}
                <DeleteContactButton
                  contactId={contact.id}
                  onDeleted={refetch}
                />
              </div>

              {isExpanded && (
                <div className="pl-14 pb-3 space-y-1 text-sm">
                  {contact.email_jsonb?.map(
                    (e, i) =>
                      e.email && (
                        <div key={i} className="flex items-center gap-2">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          <a
                            href={`mailto:${e.email}`}
                            className="text-primary hover:underline"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            {e.email}
                          </a>
                          <span className="text-xs text-muted-foreground">
                            {e.type}
                          </span>
                        </div>
                      ),
                  )}
                  {contact.phone_jsonb?.map(
                    (p, i) =>
                      p.number && (
                        <div key={i} className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span>{p.number}</span>
                          <span className="text-xs text-muted-foreground">
                            {p.type}
                          </span>
                        </div>
                      ),
                  )}
                  {contact.linkedin_url && (
                    <div className="flex items-center gap-2">
                      <Linkedin className="h-3 w-3 text-muted-foreground" />
                      <a
                        href={contact.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline truncate"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        {contact.linkedin_url.replace(
                          /^https?:\/\/(www\.)?linkedin\.com\//,
                          "",
                        )}
                      </a>
                    </div>
                  )}
                  {!hasEmail && !hasPhone && !hasLinkedIn && (
                    <span className="text-muted-foreground">
                      No contact info available
                    </span>
                  )}
                  <div className="pt-1">
                    <RouterLink
                      to={`/contacts/${contact.id}/show`}
                      state={{ from: location.pathname }}
                      className="text-xs text-primary hover:underline"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      View full profile
                    </RouterLink>
                  </div>
                </div>
              )}
            </div>
          </RecordContextProvider>
        );
      })}
    </div>
  );
};

const DeleteContactButton = ({
  contactId,
  onDeleted,
}: {
  contactId: number | string;
  onDeleted: () => void;
}) => {
  const [deleteOne, { isPending }] = useDelete();
  const [confirming, setConfirming] = useState(false);
  const notify = useNotify();

  if (confirming) {
    return (
      <div
        className="flex items-center gap-1 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            deleteOne(
              "contacts",
              { id: contactId },
              {
                onSuccess: () => {
                  notify("Contact deleted", { type: "success" });
                  onDeleted();
                },
                onError: () =>
                  notify("Failed to delete contact", { type: "error" }),
              },
            );
            setConfirming(false);
          }}
          disabled={isPending}
          className="text-xs text-destructive hover:underline"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-xs text-muted-foreground hover:underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setConfirming(true);
      }}
      className="p-1.5 text-muted-foreground hover:text-destructive transition-colors shrink-0"
      title="Delete contact"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
};

const CreateRelatedContactButton = () => {
  const company = useRecordContext<Company>();
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/contacts/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />
        Add contact
      </RouterLink>
    </Button>
  );
};

const EntitiesIterator = () => {
  const { data: entitiesList, error, isPending, refetch } =
    useListContext<Entity>();

  // Fetch entity IDs in active deals
  const { data: activeDeals } = useQuery({
    queryKey: ["active_deal_entity_ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("entity_ids")
        .eq("type", "account")
        .is("archived_at", null);
      if (error) throw error;
      return data as { entity_ids: number[] | null }[];
    },
    staleTime: 30_000,
  });

  const inDealSet = useMemo(() => {
    const ids = new Set<number>();
    for (const d of activeDeals ?? []) {
      for (const id of d.entity_ids ?? []) ids.add(id);
    }
    return ids;
  }, [activeDeals]);

  if (isPending || error) return null;
  if (!entitiesList?.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        No entities linked to this company. Entity data is populated by the scraper.
      </div>
    );
  }

  return (
    <div>
      {entitiesList.map((entity) => (
        <div
          key={entity.id}
          className="flex items-center hover:bg-muted py-2 px-4 transition-colors text-sm"
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium">{entity.name}</div>
            <div className="text-sm text-muted-foreground">
              {entity.state || "Unknown state"}
              {entity.planted_area_ha
                ? ` · ${entity.planted_area_ha.toLocaleString()} Ha`
                : ""}
            </div>
          </div>
          <DeleteEntityButton
            entityId={entity.id}
            inDeal={inDealSet.has(Number(entity.id))}
            onDeleted={refetch}
          />
        </div>
      ))}
    </div>
  );
};

const DeleteEntityButton = ({
  entityId,
  inDeal,
  onDeleted,
}: {
  entityId: number | string;
  inDeal: boolean;
  onDeleted: () => void;
}) => {
  const [deleteOne, { isPending }] = useDelete();
  const [confirming, setConfirming] = useState(false);
  const notify = useNotify();

  if (inDeal) {
    return (
      <span
        className="p-1.5 text-muted-foreground/30 shrink-0"
        title="Linked to active deal"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (confirming) {
    return (
      <div
        className="flex items-center gap-1 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            deleteOne(
              "entities",
              { id: entityId },
              {
                onSuccess: () => {
                  notify("Entity deleted", { type: "success" });
                  onDeleted();
                },
                onError: () =>
                  notify("Failed to delete entity", { type: "error" }),
              },
            );
            setConfirming(false);
          }}
          disabled={isPending}
          className="text-xs text-destructive hover:underline"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-xs text-muted-foreground hover:underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setConfirming(true);
      }}
      className="p-1.5 text-muted-foreground hover:text-destructive transition-colors shrink-0"
      title="Delete entity"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
};

const DealsIterator = () => {
  const { data: deals, error, isPending } = useListContext<Deal>();
  const { dealStages } = useConfigurationContext();
  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div>
      <div>
        {deals.map((deal) => (
          <div key={deal.id} className="p-0 text-sm">
            <RouterLink
              to={`/deals/${deal.id}/show`}
              className="flex items-center justify-between hover:bg-muted py-2 px-4 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">{deal.name}</div>
                <div className="text-sm text-muted-foreground">
                  {findDealLabel(dealStages, deal.stage)},{" "}
                  {deal.amount.toLocaleString("en-MY", {
                    notation: "compact",
                    style: "currency",
                    currency: "MYR",
                    currencyDisplay: "narrowSymbol",
                    maximumFractionDigits: 2,
                  })}
                  {deal.deal_category ? `, ${deal.deal_category}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">
                  last activity {formatDistance(deal.updated_at, now)} ago{" "}
                </div>
              </div>
            </RouterLink>
          </div>
        ))}
      </div>
    </div>
  );
};
